import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import api from "../lib/api";
import { toast } from "sonner";

const ManualImportDialog = ({ open, onOpenChange, onImported }) => {
    // Single listing form
    const [single, setSingle] = useState({
        title: "",
        original_url: "",
        source_name: "",
        asking_price_eur: "",
        living_area_sqm: "",
        bedrooms: "",
        bathrooms: "",
        municipality: "",
        neighbourhood: "",
        sea_view: "unknown",
        photo_urls: "",
        description: "",
    });
    const [csv, setCsv] = useState("");
    const [saving, setSaving] = useState(false);

    const submitSingle = async () => {
        setSaving(true);
        try {
            const payload = {
                ...single,
                asking_price_eur: single.asking_price_eur ? parseFloat(single.asking_price_eur) : null,
                living_area_sqm: single.living_area_sqm ? parseFloat(single.living_area_sqm) : null,
                bedrooms: single.bedrooms ? parseInt(single.bedrooms, 10) : null,
                bathrooms: single.bathrooms ? parseInt(single.bathrooms, 10) : null,
            };
            await api.post("/listings/manual", payload);
            toast.success("Listing imported", {
                description:
                    "Verification status set to 'Needs Local Verification' — clearly tagged as manual data.",
            });
            onImported?.();
            onOpenChange(false);
        } catch (e) {
            toast.error("Could not import listing", {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setSaving(false);
        }
    };

    const submitCsv = async () => {
        if (!csv.trim()) {
            toast.error("Paste CSV content first");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/listings/import/csv", { csv });
            toast.success(
                `Imported ${data.created} listings`,
                {
                    description: `Skipped ${data.skipped_duplicates} duplicates · ${data.errors.length} errors`,
                }
            );
            if (data.errors.length) {
                console.warn("Import errors:", data.errors);
            }
            onImported?.();
            onOpenChange(false);
        } catch (e) {
            toast.error("CSV import failed", {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setSaving(false);
        }
    };

    const set = (k) => (e) => setSingle((s) => ({ ...s, [k]: e.target.value }));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manual-import-dialog">
                <DialogHeader>
                    <DialogTitle>Manual import</DialogTitle>
                    <DialogDescription>
                        For sources that block scraping or aren't yet wired. Listings come
                        in tagged as <em>Needs Local Verification</em> so they're never
                        confused with scraper-verified data.
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="single" className="mt-2">
                    <TabsList>
                        <TabsTrigger value="single" data-testid="tab-import-single">Single listing</TabsTrigger>
                        <TabsTrigger value="csv" data-testid="tab-import-csv">CSV upload</TabsTrigger>
                    </TabsList>
                    <TabsContent value="single" className="space-y-3 mt-4">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <F label="Source name *">
                                <Input
                                    value={single.source_name}
                                    onChange={set("source_name")}
                                    placeholder="e.g. JamesEdition (manual import)"
                                    data-testid="ml-source-name"
                                />
                            </F>
                            <F label="Original URL *">
                                <Input
                                    type="url"
                                    value={single.original_url}
                                    onChange={set("original_url")}
                                    placeholder="https://…"
                                    data-testid="ml-original-url"
                                />
                            </F>
                            <F label="Title">
                                <Input value={single.title} onChange={set("title")} data-testid="ml-title" />
                            </F>
                            <F label="Municipality">
                                <Input value={single.municipality} onChange={set("municipality")} data-testid="ml-muni" />
                            </F>
                            <F label="Neighbourhood">
                                <Input value={single.neighbourhood} onChange={set("neighbourhood")} />
                            </F>
                            <F label="Sea view">
                                <select
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                    value={single.sea_view}
                                    onChange={set("sea_view")}
                                    data-testid="ml-sea-view"
                                >
                                    <option value="unknown">Unknown</option>
                                    <option value="yes">Yes</option>
                                    <option value="partial">Partial</option>
                                    <option value="no">No</option>
                                </select>
                            </F>
                            <F label="Asking price (EUR)">
                                <Input type="number" value={single.asking_price_eur} onChange={set("asking_price_eur")} data-testid="ml-price" />
                            </F>
                            <F label="Living area (m²)">
                                <Input type="number" value={single.living_area_sqm} onChange={set("living_area_sqm")} data-testid="ml-area" />
                            </F>
                            <F label="Bedrooms">
                                <Input type="number" value={single.bedrooms} onChange={set("bedrooms")} />
                            </F>
                            <F label="Bathrooms">
                                <Input type="number" value={single.bathrooms} onChange={set("bathrooms")} />
                            </F>
                        </div>
                        <F label="Photo URLs (one per line)">
                            <Textarea rows={2} value={single.photo_urls} onChange={set("photo_urls")} placeholder="https://…&#10;https://…" />
                        </F>
                        <F label="Description">
                            <Textarea rows={3} value={single.description} onChange={set("description")} />
                        </F>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                onClick={submitSingle}
                                disabled={saving || !single.source_name || !/^https?:\/\//.test(single.original_url)}
                                data-testid="ml-submit-single"
                            >
                                {saving ? "Importing…" : "Import listing"}
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    <TabsContent value="csv" className="space-y-3 mt-4">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            Required columns: <code className="font-mono">original_url</code>,{" "}
                            <code className="font-mono">source_name</code>. Optional: title, asking_price_eur,
                            living_area_sqm, bedrooms, bathrooms, municipality, neighbourhood, sea_view,
                            year_built, renovation_status, distance_to_sea_m, photo_urls.
                        </p>
                        <Textarea
                            rows={14}
                            value={csv}
                            onChange={(e) => setCsv(e.target.value)}
                            placeholder={"original_url,source_name,title,asking_price_eur,living_area_sqm,bedrooms,municipality,sea_view\nhttps://www.example.com/listing/1,Manual Agency,Apartment near Marina,290000,72,2,Tivat,yes\n…"}
                            className="font-mono text-xs"
                            data-testid="ml-csv"
                        />
                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                                Cancel
                            </Button>
                            <Button onClick={submitCsv} disabled={saving || !csv.trim()} data-testid="ml-submit-csv">
                                {saving ? "Importing…" : "Import CSV"}
                            </Button>
                        </DialogFooter>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
            {label}
        </Label>
        {children}
    </div>
);

export default ManualImportDialog;
